import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray, option } from 'fp-ts';
import { LoyaltyProgram, LoyaltyProgramType, LoyaltyTier } from '@mews-commander/data-access';
import { Identifiable } from '@mews/std';
import { MultiSelectOption } from '@optimus-web/core';
import { SortableLoyaltyItem, getLoyaltyMembershipName, loyaltySortingOrds } from '@mews-commander/utils';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { useLoyaltyProgramsGetAllQuery } from '../../common';

type LoyaltyProgramItem = SortableLoyaltyItem &
    Identifiable & {
        tierId: string | null;
        tierName: string | null;
    };

export const useLoyaltyFilterOptions = () => {
    const { rawPrograms, rawTiers } = useLoyaltyProgramsGetAllQuery();
    const hideUnknownLoyaltyPrograms = useFeatureFlag(FeatureFlag.hideUnknownLoyaltyPrograms);

    return useMemo(() => {
        const programTiers = pipe(
            rawTiers,
            option.fromNullable,
            option.flatMap(option.fromPredicate(array.isNonEmpty)),
            option.map(nonEmptyArray.groupBy<LoyaltyTier>((tier) => tier.loyaltyProgramId)),
            option.getOrElse<Record<string, Array<LoyaltyTier>>>(() => ({}))
        );

        return pipe(
            rawPrograms || [],
            array.flatMap<LoyaltyProgram, LoyaltyProgramItem>(({ id, name, type }) => {
                /**
                 * Issue: As we use sorting of program's options based on related tier ordering as a secons rule,
                 * the program's option without tier appear on top of all the programs list (if we will use 0 as
                 * default tierOrdering) or somewhere in the middle of the list (if we will use Number.MAX_SAFE_INTEGER).
                 * Both ways same program options will be displayed far away of the same program options with tier.
                 *
                 * Solution: We search for minimal tier ordering value from the same program's tiers and will use it for
                 * this program option without tier ("minProgramTiersOrdering").
                 * This way the program option without tier will be displayed on top of the program with tier (if it exists).
                 */
                const minProgramTiersOrdering = Math.min(...(programTiers[id]?.map((tier) => tier.ordering) || [0]));

                const result: Array<LoyaltyProgramItem> = [
                    {
                        id,
                        programName: name,
                        programType: type,
                        tierId: null,
                        tierName: null,
                        tierOrdering: minProgramTiersOrdering,
                    },
                ];

                programTiers[id]?.forEach((tier) =>
                    result.push({
                        id,
                        programName: name,
                        programType: type,
                        tierId: tier.id,
                        tierName: tier.name,
                        tierOrdering: tier.ordering,
                    })
                );

                return result;
            }),
            array.filter(({ programType }) =>
                hideUnknownLoyaltyPrograms ? programType !== LoyaltyProgramType.Unknown : true
            ),
            array.sortBy(loyaltySortingOrds),
            array.map(
                ({ id: loyaltyProgramId, programName, tierId: loyaltyTierId, tierName }) =>
                    ({
                        value: JSON.stringify({
                            loyaltyProgramId,
                            loyaltyTierId,
                        }),
                        label: getLoyaltyMembershipName({ programName, tierName }),
                    }) satisfies MultiSelectOption
            )
        );
    }, [rawPrograms, rawTiers, hideUnknownLoyaltyPrograms]);
};
